import React from "react";
import * as Ariakit from "@ariakit/react";
import styled from "styled-components";
import { Color } from "../../constants/color";
import { ZIndex } from "../../constants/z-index";

/*
 Suggerimento appeso a un controllo: spiega che cosa fa il bottone senza occupare
 spazio nel modulo. Il `title` nativo faceva lo stesso lavoro ma con il ritardo e
 i colori del sistema operativo, fuori dal resto dell'applicazione.
 */

const Bolla = styled(Ariakit.Tooltip)`
  z-index: ${ZIndex.TOOLTIP};
  max-width: 20rem;
  padding: 0.5rem 0.8rem;
  border-radius: 0.6rem;
  border: 1px solid ${Color.LIGHT_BLUE};
  background-color: ${Color.WHITE};
  color: ${Color.DARK_GREY};
  box-shadow: 0 0 6px 3px ${Color.SHADOW};
  font-family: "Avenir Next Regular", sans-serif;
  font-size: 13px;
  line-height: 1.35;
  text-align: left;
  text-transform: none;
  font-weight: normal;
`;

const Punta = styled(Ariakit.TooltipArrow)`
  fill: ${Color.WHITE};
  stroke: ${Color.LIGHT_BLUE};
`;

/*
 L'ancora e' un contenitore e non il controllo stesso: i bottoni dell'applicazione sono
 componenti senza forwardRef, e Ariakit ha bisogno di un nodo vero a cui agganciare la
 bolla. Lo span aderisce al contenuto, quindi l'area sensibile resta quella del controllo.
 */
const Ancora = styled.span`
  display: inline-flex;
  width: max-content;
`;

/*
 `style` va sull'ancora: quando il controllo era figlio diretto di un flex, i margini
 che lo posizionavano (es. `marginLeft: "auto"`) passano al contenitore.
 Senza testo il controllo non ha suggerimento ma resta nello stesso contenitore, cosi'
 un bottone che ne ha uno solo in certi stati non cambia posto quando lo perde.
 */
interface Props {
  testo: string | null;
  children: React.ReactNode;
  style?: React.CSSProperties;
}

const Suggerimento = ({ testo, children, style }: Props) => {
  if (!testo) {
    return <Ancora style={style}>{children}</Ancora>;
  }
  return (
    <ConBolla testo={testo} style={style}>
      {children}
    </ConBolla>
  );
};

/*
 L'ancora resta fuori dal giro del Tab (`tabIndex={-1}`): e' lo span attorno al controllo, e
 Ariakit la renderebbe focalizzabile aggiungendo una tappa vuota prima di ogni controllo.
 Non si usa `focusable={false}`, che spegne anche l'apertura al passaggio del mouse.
 La bolla deve aprirsi pure per chi naviga da tastiera, quando il focus arriva al
 controllo: il focus risale dal controllo allo span, quindi basta ascoltarlo qui.
 */
/*
 Solo il focus da tastiera apre la bolla: dopo un clic il controllo tiene il focus, e la
 bolla resterebbe aperta sopra quello che il clic ha aperto (una modale, un'altra pagina).
 jsdom non conosce `:focus-visible` e lancia: li' la bolla semplicemente non si apre.
 */
function focoDaTastiera(elemento: Element): boolean {
  try {
    return elemento.matches(":focus-visible");
  } catch {
    return false;
  }
}

const ConBolla = ({
  testo,
  children,
  style,
}: Props & { testo: string }) => {
  const bolla = Ariakit.useTooltipStore({ placement: "top", timeout: 250 });
  return (
    <>
      <Ariakit.TooltipAnchor
        store={bolla}
        tabIndex={-1}
        render={<Ancora style={style} />}
        onFocus={(event) => {
          if (focoDaTastiera(event.target)) bolla.show();
        }}
        onBlur={() => bolla.hide()}
      >
        {children}
      </Ariakit.TooltipAnchor>
      <Bolla store={bolla} gutter={8}>
        <Punta />
        {testo}
      </Bolla>
    </>
  );
};

export default Suggerimento;
